import { useState } from "react";
import { X, AlertTriangle } from "lucide-react";

export default function DeleteListDialog({ open, list, taskCount, otherLists, onConfirm, onClose }) {
  // otherLists: lists.filter(l => l.id !== list.id)  — must always have at least 1 (defaults)
  const [mode, setMode] = useState("move"); // 'move' | 'delete'
  const [targetListId, setTargetListId] = useState(otherLists?.[0]?.id || "");
  const [busy, setBusy] = useState(false);

  if (!open || !list) return null;

  const submit = async () => {
    setBusy(true);
    try {
      if (taskCount === 0) {
        await onConfirm({ list, mode: "empty" });
      } else if (mode === "delete") {
        await onConfirm({ list, mode: "delete_tasks" });
      } else {
        if (!targetListId) return;
        await onConfirm({ list, mode: "move", targetListId });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="delete-list-overlay"
    >
      <div
        className="bg-white border border-zinc-200 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Delete "{list.name}"?
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="delete-list-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {taskCount === 0 ? (
            <p className="text-sm text-zinc-700">
              This list is empty. It will be deleted permanently.
            </p>
          ) : (
            <>
              <p className="text-sm text-zinc-700">
                This list still has <strong>{taskCount} task{taskCount === 1 ? "" : "s"}</strong>.
                Pick one of:
              </p>
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${
                    mode === "move" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid="delete-list-option-move"
                >
                  <input
                    type="radio"
                    name="dl-mode"
                    value="move"
                    checked={mode === "move"}
                    onChange={() => setMode("move")}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-zinc-900">Move tasks to another list</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Keep your work — relocate the tasks first, then delete the list.</div>
                    {mode === "move" && (
                      <select
                        value={targetListId}
                        onChange={(e) => setTargetListId(e.target.value)}
                        className="mt-2 w-full border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                        data-testid="delete-list-target-select"
                      >
                        {otherLists.map((l) => (
                          <option key={l.id} value={l.id}>{l.name} ({l.category})</option>
                        ))}
                      </select>
                    )}
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${
                    mode === "delete" ? "border-red-700 bg-red-50" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid="delete-list-option-delete"
                >
                  <input
                    type="radio"
                    name="dl-mode"
                    value="delete"
                    checked={mode === "delete"}
                    onChange={() => setMode("delete")}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-red-800">Delete list AND its {taskCount} task{taskCount === 1 ? "" : "s"}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Permanent. Subtasks and timer state go too.</div>
                  </div>
                </label>
              </div>
            </>
          )}
        </div>

        <footer className="border-t border-zinc-200 px-5 py-3 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900" data-testid="delete-list-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || (taskCount > 0 && mode === "move" && !targetListId)}
            className={`px-5 py-2 text-xs font-medium uppercase tracking-[0.12em] transition-colors ${
              mode === "delete" || taskCount === 0
                ? "bg-red-700 hover:bg-red-800 text-white"
                : "bg-zinc-900 hover:bg-[#002FA7] text-white"
            } disabled:opacity-40`}
            data-testid="delete-list-confirm"
          >
            {busy ? "Working…" : mode === "delete" || taskCount === 0 ? "Delete" : "Move & delete list"}
          </button>
        </footer>
      </div>
    </div>
  );
}
